import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { money, dateFmt } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck, MagnifyingGlass, XCircle, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function Verify() {
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);

  const download = async () => {
    const q = (result?.folio || code).trim();
    if (!q) return;
    setDl(true);
    try {
      const { data } = await api.get(`/verify-document/${encodeURIComponent(q)}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documento_${q}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // noop
    } finally {
      setDl(false);
    }
  };

  const verify = async (value) => {
    const q = (value ?? code).trim();
    if (!q) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(`/verify-deposit/${encodeURIComponent(q)}`);
      setResult(data);
    } catch (e) {
      setResult({ valid: false });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const c = searchParams.get("code");
    if (c) { setCode(c); verify(c); }
  }, []); // eslint-disable-line

  return (
    <div className="App bg-[#F8FAFC] min-h-screen">
      <Header />
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ShieldCheck size={26} weight="duotone" className="text-emerald-800" />
            </div>
            <h1 className="font-display text-3xl font-bold text-emerald-900" data-testid="verify-title">{t("verify_deposit")}</h1>
          </div>
          <p className="text-slate-500 mb-6">{t("verify_sub")}</p>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="verify-input"
                  className="pl-9 h-11 font-mono-num"
                  placeholder={t("verify_placeholder")}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                />
              </div>
              <Button data-testid="verify-btn" onClick={() => verify()} disabled={busy || !code.trim()} className="h-11 bg-emerald-800 hover:bg-emerald-900">
                {busy ? "…" : t("verify_btn")}
              </Button>
            </div>

            {result && result.valid && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-5" data-testid="verify-result-valid">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-3">
                  <CheckCircle size={22} weight="fill" /> {t("verify_valid")}
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <span className="text-slate-500">{t("folio")}</span><span className="font-mono-num font-semibold text-emerald-900">{result.folio || "—"}</span>
                  <span className="text-slate-500">{t("account_number")}</span><span className="font-mono-num font-semibold text-emerald-900">{result.account_number}</span>
                  <span className="text-slate-500">{t("holder")}</span><span className="font-semibold text-emerald-900">{result.holder}</span>
                  {result.type === "savings" ? (
                    <>
                      <span className="text-slate-500">{t("verify_savings")}</span><span className="font-semibold text-emerald-900">✓</span>
                      <span className="text-slate-500">{t("verify_balance")}</span><span className="font-mono-num font-bold text-emerald-900">{money(result.principal, result.currency)}</span>
                    </>
                  ) : result.type === "loan" ? (
                    <>
                      <span className="text-slate-500">{t("verify_loan")}</span><span className="font-semibold text-emerald-900">✓</span>
                      <span className="text-slate-500">{t("verify_amount")}</span><span className="font-mono-num font-bold text-emerald-900">{money(result.principal, result.currency)}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-slate-500">{t("deposit_amount")}</span><span className="font-mono-num font-semibold text-emerald-900">{money(result.principal, result.currency)} · {result.annual_rate}%</span>
                      <span className="text-slate-500">{t("maturity")}</span><span className="font-mono-num font-semibold text-emerald-900">{dateFmt(result.end_date, lang)}</span>
                      <span className="text-slate-500">{t("current_value")}</span><span className="font-mono-num font-bold text-emerald-900">{money(result.maturity_value, result.currency)}</span>
                    </>
                  )}
                </div>
                {result.downloadable && (
                  <Button
                    data-testid="verify-download-btn"
                    onClick={download}
                    disabled={dl}
                    variant="outline"
                    className="mt-4 w-full h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-100 gap-2"
                  >
                    <DownloadSimple size={18} weight="bold" /> {dl ? "…" : t("verify_download")}
                  </Button>
                )}
              </motion.div>
            )}

            {result && !result.valid && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-lg border border-red-200 bg-red-50 p-5 flex items-center gap-2 text-red-700" data-testid="verify-result-invalid">
                <XCircle size={22} weight="fill" /> {t("verify_invalid")}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
